import { useEffect, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { FormattedMessage } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";

import { useLocation } from "@/utils/location";
import { logger } from "@/common/logger";
import { toast } from "@/utils/toast";
import { relayErrorMessage } from "@/utils/relay";
import Loading from "@/components/Loading";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  Settings01Icon,
  CheckCircleIcon,
  Trophy01Icon,
  FileCode01Icon,
} from "@/icons";
import LogoIcon from "@/components/LogoIcon";
import { Button } from "@/kit/ui/button";
import {
  AuthHeader,
  AuthHeaderIcon,
  AuthHeaderIconBadge,
  AuthHeaderTitle,
  AuthHeaderDescription,
  AuthContent,
} from "@/components/AuthLayout";
import ErrorPage from "./ErrorPage";

import { OAuth2AuthorizePageMutation as OAuth2AuthorizePageMutationType } from "./__generated__/OAuth2AuthorizePageMutation.graphql";
import {
  useOAuth2AuthorizePageQuery,
  Scope,
} from "./loaders/OAuth2AuthorizePage";

const OAuth2AuthorizePageMutation = graphql`
  mutation OAuth2AuthorizePageMutation(
    $input: Oauth2AuthorizeInput!
    $consent: Boolean!
  ) {
    oauth2Authorize(input: $input, consent: $consent) {
      unauthorized
      clientError
      redirectUri
    }
  }
`;

interface OAuth2ConsentProps {
  clientId: string;
  name?: string;
  description?: string;
  scope: readonly Scope[];
  loading: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const LOCALHOST_PREFIX = "localhost-";

const scopeDescription = (scope: Scope) => {
  switch (scope) {
    case "ALL":
      return [
        <AlertCircleIcon />,
        <FormattedMessage defaultMessage="This app will have full access to your account" />,
      ];
    case "DEFAULT":
      return [
        <Settings01Icon />,
        <FormattedMessage defaultMessage="This app will have access to your basic account info" />,
      ];
    case "READ_COMPETITION":
      return [
        <Trophy01Icon />,
        <FormattedMessage defaultMessage="This app will have read access to your competitions" />,
      ];
    case "READ_PROJECT":
      return [
        <FileCode01Icon />,
        <FormattedMessage defaultMessage="This app will have read access to use cases and submissions" />,
      ];
  }
};

const OAuth2Consent = ({
  clientId,
  name,
  description,
  scope,
  loading,
  onAccept,
  onDecline,
}: OAuth2ConsentProps) => {
  return (
    <>
      <AuthHeader>
        <AuthHeaderIcon>
          <AuthHeaderIconBadge>
            {loading ? (
              <div className="w-8">
                <Loading />
              </div>
            ) : (
              <LogoIcon />
            )}
          </AuthHeaderIconBadge>
        </AuthHeaderIcon>
        <AuthHeaderTitle className="font-medium">
          <FormattedMessage
            defaultMessage="Connecting {name} to {aqora}"
            values={{
              name: name ? (
                <b>{name}</b>
              ) : clientId.startsWith(LOCALHOST_PREFIX) ? (
                <FormattedMessage
                  defaultMessage="computer {name}"
                  values={{
                    name: <b>{clientId.substring(LOCALHOST_PREFIX.length)}</b>,
                  }}
                />
              ) : (
                <b>{clientId}</b>
              ),
              aqora: <b>Aqora</b>,
            }}
          />
        </AuthHeaderTitle>
        {description && (
          <AuthHeaderDescription>{description}</AuthHeaderDescription>
        )}
      </AuthHeader>
      <AuthContent className="space-y-8">
        {scope.includes("ALL") && (
          <div className="flex flex-row items-center space-x-4 bg-warning-50 border border-warning-500 rounded-md p-2">
            <div className="text-warning-500">
              <AlertTriangleIcon />
            </div>
            <div>
              <FormattedMessage defaultMessage="This app will have full access to your account. If you did not originate this request do not click Allow below." />
            </div>
          </div>
        )}
        <div className="space-y-4">
          <h3 className="font-semibold uppercase text-grey-400">
            <FormattedMessage defaultMessage="Permissions" />
          </h3>
          {scope.map((scope) => {
            const [icon, description] = scopeDescription(scope);
            return (
              <div key={scope} className="flex flex-row items-center space-x-4">
                <div className="text-grey-400">{icon}</div>
                <div>{description}</div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="secondary"
            onClick={() => onDecline()}
            disabled={loading}
          >
            <FormattedMessage defaultMessage="Deny Access" />
          </Button>
          <Button onClick={() => onAccept()} disabled={loading}>
            <FormattedMessage defaultMessage="Allow" />
          </Button>
        </div>
      </AuthContent>
    </>
  );
};

const OAuth2Success = ({ approved }: { approved: boolean }) => {
  const [closingMs, setClosingMs] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (window.opener != null) {
      return;
    }
    const closingTime = Date.now() + 5000;
    setClosingMs(closingTime - Date.now());
    const interval = setInterval(() => {
      const newClosingMs = closingTime - Date.now();
      setClosingMs(newClosingMs);
      if (newClosingMs <= 0) {
        window.close();
      }
    }, 100);
    () => clearInterval(interval);
  }, [setClosingMs]);
  return (
    <div className="container mx-auto min-h-full flex justify-center items-center px-4 py-4">
      <>
        <AuthHeader>
          <AuthHeaderIcon>
            <AuthHeaderIconBadge>
              <CheckCircleIcon className="text-success-500" />
            </AuthHeaderIconBadge>
          </AuthHeaderIcon>
          <>
            <AuthHeaderTitle>
              {approved ? (
                <FormattedMessage defaultMessage="Access granted!" />
              ) : (
                <FormattedMessage defaultMessage="Access denied" />
              )}
            </AuthHeaderTitle>
            <AuthHeaderDescription>
              <p>
                <FormattedMessage defaultMessage="You can leave this window and go back to your terminal" />
              </p>
              {closingMs !== undefined && closingMs > 0 && (
                <p>
                  <FormattedMessage
                    defaultMessage="(closing in {closingSecs} seconds)"
                    values={{ closingSecs: Math.ceil(closingMs / 1000) }}
                  />
                </p>
              )}
            </AuthHeaderDescription>
          </>
        </AuthHeader>
      </>
    </div>
  );
};

export default function OAuth2AuthorizePage() {
  const {
    query: { viewer, oauth2Authorize },
    variables: { input },
  } = useOAuth2AuthorizePageQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, _setSearchParams] = useSearchParams();
  const [commitMutation, isMutationInFlight] =
    useMutation<OAuth2AuthorizePageMutationType>(OAuth2AuthorizePageMutation);
  const [approved, setApproved] = useState<boolean | undefined>(undefined);

  const valid = !!oauth2Authorize?.valid;

  useEffect(() => {
    if (!valid && oauth2Authorize?.redirectUri) {
      location.replace(oauth2Authorize.redirectUri);
    }
  }, [location, valid, oauth2Authorize?.redirectUri]);

  useEffect(() => {
    if (valid && !viewer) {
      return navigate({
        pathname: "/login",
        search: searchParams.toString(),
      });
    }
  }, [valid, viewer, navigate, searchParams]);

  const submit = (consent: boolean) => {
    commitMutation({
      variables: {
        input,
        consent,
      },
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
      onCompleted: ({
        oauth2Authorize: { unauthorized, clientError, redirectUri },
      }) => {
        if (unauthorized) {
          return navigate({
            pathname: "/login",
            search: searchParams.toString(),
          });
        }
        if (clientError || redirectUri == null) {
          return toast.error(
            <FormattedMessage defaultMessage="An error occurred" />,
          );
        }
        let parsedUri: URL;
        try {
          parsedUri = new URL(redirectUri);
        } catch (err) {
          return toast.error(
            <FormattedMessage defaultMessage="Could not redirect" />,
          );
        }
        if (parsedUri.host == "aqora.io") {
          setApproved(consent);
        } else {
          location.replace(redirectUri);
        }
      },
    });
  };

  if (!valid) {
    return <ErrorPage status={400} />;
  }

  return (
    <div className="container mx-auto min-h-full flex justify-center items-center px-4 py-4">
      <Toaster position="top-center" />
      <div className="w-full max-w-md flex flex-col">
        {approved !== undefined ? (
          <OAuth2Success approved={approved} />
        ) : (
          <OAuth2Consent
            clientId={input.clientId}
            name={oauth2Authorize.oauth2Config?.name}
            description={oauth2Authorize.oauth2Config?.description || undefined}
            scope={oauth2Authorize.scope}
            loading={isMutationInFlight}
            onAccept={() => submit(true)}
            onDecline={() => submit(false)}
          />
        )}
      </div>
    </div>
  );
}
